import BaseModel from './BaseModel';

const schema = {
  $schema: 'http://json-schema.org/draft/2020-12/schema',
  title: 'Notice',
  type: 'object',
  properties: {
    _id: {
      type: 'string',
      description: 'Unique identifier for the notice',
    },
    type: {
      type: 'string',
      enum: ['info', 'warning', 'success'],
      description: 'Severity/type of notice',
    },
    subject: {
      type: 'object',
      description: 'Subject line in multiple languages',
      patternProperties: {
        '^[a-z]{2}$': {
          type: 'string',
        },
      },
      minProperties: 1,
    },
    content: {
      type: 'object',
      description: 'Content body in multiple languages',
      patternProperties: {
        '^[a-z]{2}$': {
          type: 'string',
        },
      },
      minProperties: 1,
    },
    author: {
      type: 'string',
      description: 'Author or team who published the notice',
    },
    dateStart: {
      type: 'string',
      format: 'date',
      description: 'Start date of validity (YYYY-MM-DD)',
    },
    dateEnd: {
      type: 'string',
      format: 'date',
      description: 'End date of validity (YYYY-MM-DD)',
    },
    timeStart: {
      type: 'string',
      pattern: '^\\d{2}:\\d{2}:\\d{2}$',
      description: 'Start time of validity (HH:mm:ss)',
    },
    timeEnd: {
      type: 'string',
      pattern: '^\\d{2}:\\d{2}:\\d{2}$',
      description: 'End time of validity (HH:mm:ss)',
    },
    timezone: {
      type: 'string',
      description: 'IANA timezone name (e.g., Pacific/Auckland)',
    },
    createdAt: {
      bsonType: 'date',
      description: 'Timestamp when the notice was created',
    },
    updatedAt: {
      bsonType: 'date',
      description: 'Timestamp when the notice was last updated',
    },
    createdBy: {
      type: 'string',
      description: 'User ID who created the notice',
    },
    updatedBy: {
      type: 'string',
      description: 'User ID who last updated the notice',
    },
  },
  required: [
    '_id',
    'type',
    'subject',
    'content',
    'author',
    'dateStart',
    'dateEnd',
    'timeStart',
    'timeEnd',
    'timezone',
  ],
  additionalProperties: false,
};

/**
 * @typedef {object} NoticeBoardDoc
 * @property {string} _id - Unique identifier for the notice
 * @property {'info'|'warning'|'success'} type - Severity/type of notice
 * @property {Record<string, string>} subject - Subject line in multiple languages
 * @property {Record<string, string>} content - Content body in multiple languages
 * @property {string} author - Author or team who published the notice
 * @property {string} dateStart - Start date of validity (YYYY-MM-DD)
 * @property {string} dateEnd - End date of validity (YYYY-MM-DD)
 * @property {string} timeStart - Start time of validity (HH:mm:ss)
 * @property {string} timeEnd - End time of validity (HH:mm:ss)
 * @property {string} timezone - IANA timezone name (e.g., Pacific/Auckland)
 * @property {Date} [createdAt] - Timestamp when the notice was created
 * @property {Date} [updatedAt] - Timestamp when the notice was last updated
 * @property {string} [createdBy] - User ID who created the notice
 * @property {string} [updatedBy] - User ID who last updated the notice
 */

/**
 * Represents a NoticeBoard entry.
 * @class
 * @extends {BaseModel<NoticeBoardDoc>}
 */
class NoticeBoard extends BaseModel {}

NoticeBoard.collection = 'NoticeBoard';
NoticeBoard.scopeName = 'noticeBoard';
NoticeBoard.schema = schema;

export default NoticeBoard;
